/**
 * Workflow-specific wrapper for the generic WaterfallChart.
 *
 * @module FlowDesigner/runs/RunWaterfallView
 */

import React from 'react';
import { WaterfallChart, type WaterfallEntry } from '@chatsdet/ui';
import type { WorkflowRunWithStepsDto } from '../hooks/useWorkflowRun';
import type { WorkflowRunThoughtDto } from '../hooks/useWorkflowRunThoughts';
import type { WorkflowNode } from '../../../../types/workflow';
import { useWaterfallData } from './useWaterfallData';

type RunWaterfallViewProps = {
  run: WorkflowRunWithStepsDto;
  thoughts: WorkflowRunThoughtDto[] | null | undefined;
  nodes: WorkflowNode[];
  /** Called when user clicks a step bar, to navigate to step detail */
  onStepSelect?: (stepId: string) => void;
  className?: string;
};

export function RunWaterfallView({
  run,
  thoughts,
  nodes,
  onStepSelect,
  className,
}: RunWaterfallViewProps) {
  const { entries, totalDurationMs } = useWaterfallData(run, thoughts, nodes);

  const handleEntryClick = (entry: WaterfallEntry) => {
    onStepSelect?.(entry.id);
  };

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
        No timing data available for this run
      </div>
    );
  }

  return (
    <WaterfallChart
      entries={entries}
      totalDurationMs={totalDurationMs}
      onEntryClick={handleEntryClick}
      className={className}
    />
  );
}

RunWaterfallView.displayName = 'RunWaterfallView';
